import React, { useEffect, useState, useRef } from "react";
import { uploadPost, fetchPostCids, fetchPostByCid } from "./api";
import { Layout, Form, Input, Button, List, Typography, message, Spin, Space, Switch, Dropdown, Menu, Upload, Tooltip, Card, Skeleton, Pagination, Select } from "antd";
import "antd/dist/reset.css";
import MDEditor from "@uiw/react-md-editor";
import MarkdownPreview from "@uiw/react-markdown-preview";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import PostDetail from "./PostDetail";
import detectEthereumProvider from "@metamask/detect-provider";
import { ethers } from "ethers";
import { uploadImageToIPFS } from "./ipfsUpload";
import { BrowserProvider } from "ethers";
import LazyLoad from 'react-lazyload';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

function WalletButton({ onWalletChange, darkMode }) {
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 自动检测已连接钱包
    async function checkWallet() {
      const provider = await detectEthereumProvider();
      if (provider && window.ethereum.selectedAddress) {
        setAddress(window.ethereum.selectedAddress);
        onWalletChange && onWalletChange(window.ethereum.selectedAddress);
      }
    }
    checkWallet();
    // 监听账户切换
    if (window.ethereum) {
      window.ethereum.on("accountsChanged", (accounts) => {
        setAddress(accounts[0] || "");
        onWalletChange && onWalletChange(accounts[0] || "");
      });
    }
    return () => {
      if (window.ethereum && window.ethereum.removeListener) {
        window.ethereum.removeListener("accountsChanged", () => {});
      }
    };
  }, [onWalletChange]);

  async function connectWallet() {
    setLoading(true);
    try {
      const provider = await detectEthereumProvider();
      if (!provider) {
        message.error("请先安装 MetaMask 钱包");
        setLoading(false);
        return;
      }
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      setAddress(accounts[0]);
      onWalletChange && onWalletChange(accounts[0]);
      message.success("钱包连接成功");
    } catch (e) {
      message.error("连接钱包失败: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  }

  if (address) {
    return (
      <Dropdown
        overlay={
          <Menu>
            <Menu.Item key="address" disabled>
              {address.slice(0, 6)}...{address.slice(-4)}
            </Menu.Item>
            <Menu.Item key="copy" onClick={() => {navigator.clipboard.writeText(address); message.success("已复制地址");}}>
              复制地址
            </Menu.Item>
          </Menu>
        }
        placement="bottomRight"
      >
        <Button type="primary" style={{ background: darkMode ? '#1677ff' : undefined }}>
          {address.slice(0, 6)}...{address.slice(-4)}
        </Button>
      </Dropdown>
    );
  }

  return (
    <Button type="primary" onClick={connectWallet} loading={loading} style={{ background: darkMode ? '#1677ff' : undefined }}>
      连接钱包
    </Button>
  );
}

function ThemeToggle({ darkMode, setDarkMode }) {
  return (
    <button
      aria-label="切换暗黑模式"
      style={{
        marginLeft: 16,
        background: darkMode ? '#23232b' : '#f7f7f9',
        color: darkMode ? '#91d5ff' : '#1677ff',
        border: '1px solid #eee',
        borderRadius: 8,
        padding: '6px 18px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s',
        boxShadow: darkMode ? '0 2px 8px #1114' : '0 2px 8px #e5e5e5',
      }}
      onClick={() => {
        setDarkMode(d => {
          document.body.classList.toggle('dark', !d);
          return !d;
        });
      }}
    >
      {darkMode ? '☀️ 亮色' : '🌙 暗黑'}
    </button>
  );
}

function Home({ darkMode, wallet, posts }) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [mdValue, setMdValue] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('time');
  const pageSize = 6;
  let filteredPosts = posts.filter(p => p.title.includes(search) || p.content.includes(search));
  if (sortKey === 'time') {
    filteredPosts = filteredPosts.slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  } else if (sortKey === 'likes') {
    filteredPosts = filteredPosts.slice().sort((a, b) => (b.likes || 0) - (a.likes || 0));
  }
  const pagedPosts = filteredPosts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const mdEditorRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  async function handleSubmit(values) {
    setSubmitting(true);
    try {
      if (!wallet) {
        message.error("请先连接钱包后再发布博客");
        setSubmitting(false);
        return;
      }
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(values.title + "\n" + mdValue);
      await uploadPost({ title: values.title, content: mdValue + `\n\n<!-- signature: ${signature} -->` });
      message.success("发布成功！（已链上签名）");
      form.resetFields();
      setMdValue("");
      setLoading(true);
      const cids = await fetchPostCids();
      const postObjs = await Promise.all(
        cids.map(async (cid) => {
          try {
            const post = await fetchPostByCid(cid);
            return { ...post, cid };
          } catch (e) {
            return { cid, title: "[读取失败]", content: "", timestamp: 0 };
          }
        })
      );
      setLoading(false);
    } catch (e) {
      message.error("发布失败: " + e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleImageUpload({ file }) {
    setUploading(true);
    try {
      const url = await uploadImageToIPFS(file);
      if (url) {
        setMdValue((prev) => (prev ? prev + `\n![上传图片](${url})\n` : `![上传图片](${url})\n`));
        message.success("图片上传成功，已插入 Markdown");
      } else {
        message.error("图片上传失败");
      }
    } catch (e) {
      message.error("图片上传失败: " + (e.message || e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ background: darkMode ? "#23232b" : "#fafafa", padding: 24, borderRadius: 8, boxShadow: "0 2px 8px #eee" }}
      >
        <Form.Item name="title" label="标题" rules={[{ required: true, message: "请输入标题" }]}> 
          <Input placeholder="请输入博客标题" />
        </Form.Item>
        <Form.Item label={<span>内容 <Tooltip title="可插入图片"><Upload showUploadList={false} customRequest={handleImageUpload} accept="image/*"><Button loading={uploading} size="small" style={{ marginLeft: 8 }}>上传图片</Button></Upload></Tooltip></span>} required>
          <MDEditor
            value={mdValue}
            onChange={setMdValue}
            height={300}
            preview="edit"
            textareaProps={{ placeholder: "支持 Markdown 语法，可直接插入图片链接" }}
            data-color-mode={darkMode ? 'dark' : 'light'}
            ref={mdEditorRef}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>
            发布博客
          </Button>
        </Form.Item>
      </Form>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索博客标题或内容"
          allowClear
          value={search}
          onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          style={{ width: 260 }}
        />
        <Select
          value={sortKey}
          style={{ width: 120 }}
          onChange={v => setSortKey(v)}
          options={[
            { value: 'time', label: '按时间排序' },
            { value: 'likes', label: '按热度排序' },
          ]}
        />
      </div>
      <List
        grid={{ gutter: 24, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
        dataSource={pagedPosts}
        loading={loading}
        locale={{ emptyText: '暂无博客' }}
        renderItem={post => (
          <List.Item key={post.cid}>
            <Card
              hoverable
              style={{ borderRadius: 12, boxShadow: darkMode ? '0 2px 12px #1116' : '0 2px 12px #e5e5e5' }}
              bodyStyle={{ padding: 20 }}
              title={<Link to={`/post/${post.cid}`} style={{ fontSize: 20, color: darkMode ? '#91d5ff' : '#1677ff', fontWeight: 600 }}>{post.title}</Link>}
              extra={<span style={{ color: '#aaa', fontSize: 12 }}>{post.timestamp ? new Date(post.timestamp).toLocaleString() : ''}</span>}
            >
              <LazyLoad height={60} offset={100} once>
                <div style={{ minHeight: 60, color: darkMode ? '#eee' : '#333', fontSize: 15, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {post.content.replace(/<[^>]+>/g, '').slice(0, 80)}...
                </div>
              </LazyLoad>
              <Link to={`/post/${post.cid}`} style={{ fontSize: 14 }}>阅读全文</Link>
            </Card>
          </List.Item>
        )}
      />
      {loading ? (
        <div style={{ padding: 32 }}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Skeleton key={i} active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
          ))}
        </div>
      ) : null}
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredPosts.length}
          onChange={setCurrentPage}
          showSizeChanger={false}
        />
      </div>
    </Space>
  );
}

function MyPosts({ wallet, posts, darkMode }) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const myPosts = posts.filter(p => p.content && p.content.includes(wallet));
  const pagedPosts = myPosts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 16 }}>
      <h2 style={{ marginBottom: 24 }}>我的博客</h2>
      <List
        grid={{ gutter: 24, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
        dataSource={pagedPosts}
        locale={{ emptyText: '你还没有发布过博客' }}
        renderItem={post => (
          <List.Item key={post.cid}>
            <Card
              hoverable
              style={{ borderRadius: 12, boxShadow: darkMode ? '0 2px 12px #1116' : '0 2px 12px #e5e5e5' }}
              bodyStyle={{ padding: 20 }}
              title={<Link to={`/post/${post.cid}`} style={{ fontSize: 20, color: darkMode ? '#91d5ff' : '#1677ff', fontWeight: 600 }}>{post.title}</Link>}
              extra={<span style={{ color: '#aaa', fontSize: 12 }}>{post.timestamp ? new Date(post.timestamp).toLocaleString() : ''}</span>}
            >
              <LazyLoad height={60} offset={100} once>
                <div style={{ minHeight: 60, color: darkMode ? '#eee' : '#333', fontSize: 15, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {post.content.replace(/<[^>]+>/g, '').slice(0, 80)}...
                </div>
              </LazyLoad>
              <Link to={`/post/${post.cid}`} style={{ fontSize: 14 }}>阅读全文</Link>
            </Card>
          </List.Item>
        )}
      />
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={myPosts.length}
          onChange={setCurrentPage}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [wallet, setWallet] = useState("");
  const [posts, setPosts] = useState([]); 
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    async function loadAllPosts() {
      setLoading(true);
      try {
        const cids = await fetchPostCids();
        const postObjs = await Promise.all(
          cids.map(async (cid) => {
            try {
              const post = await fetchPostByCid(cid);
              return { ...post, cid };
            } catch (e) {
              return { cid, title: "[读取失败]", content: "", timestamp: 0 };
            }
          })
        );
        setPosts(postObjs);
      } catch (e) {
        message.error("加载博客失败: " + e.message);
      } finally {
        setLoading(false);
      }
    }
    loadAllPosts();
  }, []);

  return (
    <div data-color-mode={darkMode ? 'dark' : 'light'}>
      <Router>
        <Layout style={{ minHeight: "100vh", background: darkMode ? "#18181c" : undefined }}>
          <Header style={{ background: darkMode ? "#23232b" : "#fff", borderBottom: "1px solid #eee", display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={2} style={{ margin: 0, color: darkMode ? '#fff' : undefined }}>
              <Link to="/" style={{ color: darkMode ? '#fff' : undefined, textDecoration: 'none' }}>Web3 Blog</Link>
            </Title>
            <Space>
              <WalletButton onWalletChange={setWallet} darkMode={darkMode} />
              <Link to="/myposts">
                <Button type="link" style={{ color: darkMode ? '#91d5ff' : '#1677ff', fontWeight: 600 }}>我的博客</Button>
              </Link>
              <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />
            </Space>
          </Header>
          <Content style={{ maxWidth: 700, margin: "32px auto", width: "100%" }}>
            <Routes>
              <Route path="/" element={<Home darkMode={darkMode} wallet={wallet} posts={posts} />} />
              <Route path="/post/:cid" element={<PostDetail />} />
              <Route path="/myposts" element={<MyPosts wallet={wallet} posts={posts} darkMode={darkMode} />} />
            </Routes>
          </Content>
          <Footer style={{ textAlign: "center", color: darkMode ? "#888" : "#aaa" }}>
            Web3 Blog {new Date().getFullYear()} Powered by React, Ant Design, Cloudflare & IPFS
          </Footer>
        </Layout>
      </Router>
    </div>
  );
}

export default App;
